/**
 * DeadlineDetail Component
 *
 * Full view of a deadline with all fields, edit mode, and action buttons.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { DeadlineForm } from './DeadlineForm';
import {
  Deadline,
  DeadlineFormData,
  DEADLINE_TYPES,
  DEADLINE_PRIORITIES,
  PRIORITY_STYLES,
  STATUS_STYLES,
  RECURRENCE_PATTERNS,
  formatDate,
  formatDaysRemaining,
} from './types';
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Clock,
  FileText,
  RotateCcw,
  Sparkles,
  Edit2,
  Check,
  X,
  AlertCircle,
  Bell,
  Loader2,
} from 'lucide-react';

interface GetDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  relatedDocument?: {
    documentId: string;
    fileName: string;
    documentType: string;
  };
  error?: string;
}

interface UpdateDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  error?: string;
}

interface CompleteDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  nextDeadline?: Deadline;
  error?: string;
}

interface DismissDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  error?: string;
}

interface DeadlineDetailProps {
  caseFileId: string;
  deadlineId: string;
  onBack: () => void;
  onUpdate?: () => void;
  onExport?: (deadline: Deadline) => void;
}

export function DeadlineDetail({
  caseFileId,
  deadlineId,
  onBack,
  onUpdate,
  onExport,
}: DeadlineDetailProps): JSX.Element {
  const [deadline, setDeadline] = useState<Deadline | null>(null);
  const [relatedDocument, setRelatedDocument] = useState<{
    documentId: string;
    fileName: string;
    documentType: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [actionLoading, setActionLoading] = useState<'complete' | 'dismiss' | null>(null);

  const fetchDeadline = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const getDeadline = httpsCallable<
        { caseFileId: string; deadlineId: string },
        GetDeadlineResponse
      >(functions, 'getDeadline');

      const result = await getDeadline({ caseFileId, deadlineId });

      if (result.data.success && result.data.deadline) {
        setDeadline(result.data.deadline);
        setRelatedDocument(result.data.relatedDocument || null);
      } else {
        throw new Error(result.data.error || 'Failed to load deadline');
      }
    } catch (err) {
      console.error('Error loading deadline:', err);
      setError(err instanceof Error ? err.message : 'Failed to load deadline');
    } finally {
      setLoading(false);
    }
  }, [caseFileId, deadlineId]);

  useEffect(() => {
    fetchDeadline();
  }, [fetchDeadline]);

  const handleUpdate = async (data: DeadlineFormData) => {
    setIsUpdating(true);
    try {
      const updateDeadline = httpsCallable<
        {
          caseFileId: string;
          deadlineId: string;
          title?: string;
          description?: string;
          dueDate?: string;
          type?: string;
          priority?: string;
          alerts?: Array<{ daysBeforeDeadline: number; channels: string[]; enabled: boolean }>;
          isRecurring?: boolean;
          recurrencePattern?: string;
        },
        UpdateDeadlineResponse
      >(functions, 'updateDeadline');

      const result = await updateDeadline({
        caseFileId,
        deadlineId,
        title: data.title,
        description: data.description,
        dueDate: data.dueDate,
        type: data.type,
        priority: data.priority,
        alerts: data.alerts,
        isRecurring: data.isRecurring,
        recurrencePattern: data.recurrencePattern,
      });

      if (result.data.success && result.data.deadline) {
        setDeadline(result.data.deadline);
        setIsEditing(false);
        onUpdate?.();
      } else {
        throw new Error(result.data.error || 'Failed to update deadline');
      }
    } catch (err) {
      console.error('Error updating deadline:', err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleComplete = async () => {
    setActionLoading('complete');
    try {
      const completeDeadline = httpsCallable<
        { caseFileId: string; deadlineId: string },
        CompleteDeadlineResponse
      >(functions, 'completeDeadline');

      const result = await completeDeadline({ caseFileId, deadlineId });

      if (result.data.success) {
        if (result.data.deadline) {
          setDeadline(result.data.deadline);
        }
        onUpdate?.();
        if (result.data.nextDeadline) {
          // Show success message about next deadline
          alert(`Deadline completed! Next recurring deadline created for ${result.data.nextDeadline.dueDate}`);
        }
      } else {
        throw new Error(result.data.error || 'Failed to complete deadline');
      }
    } catch (err) {
      console.error('Error completing deadline:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete deadline');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async () => {
    const reason = prompt('Reason for dismissing (optional):');

    setActionLoading('dismiss');
    try {
      const dismissDeadline = httpsCallable<
        { caseFileId: string; deadlineId: string; reason?: string },
        DismissDeadlineResponse
      >(functions, 'dismissDeadline');

      const result = await dismissDeadline({
        caseFileId,
        deadlineId,
        reason: reason || undefined,
      });

      if (result.data.success && result.data.deadline) {
        setDeadline(result.data.deadline);
        onUpdate?.();
      } else {
        throw new Error(result.data.error || 'Failed to dismiss deadline');
      }
    } catch (err) {
      console.error('Error dismissing deadline:', err);
      setError(err instanceof Error ? err.message : 'Failed to dismiss deadline');
    } finally {
      setActionLoading(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-evergreen" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !deadline) {
    return (
      <div className="card">
        <div className="card-body">
          <button onClick={onBack} className="btn-ghost btn-sm flex items-center gap-1 mb-4">
            <ArrowLeft size={16} />
            Back to list
          </button>
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle size={24} />
            <div>
              <p className="font-medium">Error loading deadline</p>
              <p className="text-sm text-muted">{error || 'Deadline not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const priorityStyle = PRIORITY_STYLES[deadline.priority];
  const statusStyle = STATUS_STYLES[deadline.status];
  const typeLabel = DEADLINE_TYPES.find((t) => t.value === deadline.type)?.label || deadline.type;
  const priorityLabel = DEADLINE_PRIORITIES.find((p) => p.value === deadline.priority)?.label || deadline.priority;
  const isActionable = deadline.status !== 'completed' && deadline.status !== 'dismissed';

  // Edit mode
  if (isEditing) {
    return (
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="font-semibold text-ink">Edit Deadline</h3>
          <button onClick={() => setIsEditing(false)} className="btn-ghost btn-sm p-1">
            <X size={18} />
          </button>
        </div>
        <div className="card-body">
          <DeadlineForm
            initialData={{
              title: deadline.title,
              dueDate: deadline.dueDate,
              type: deadline.type,
              priority: deadline.priority,
              description: deadline.description,
              relatedDocumentId: deadline.relatedDocumentId,
              isRecurring: deadline.isRecurring,
              recurrencePattern: deadline.recurrencePattern,
              alerts: deadline.alerts.map((a) => ({
                daysBeforeDeadline: a.daysBeforeDeadline,
                channels: a.channels,
                enabled: a.enabled,
              })),
            }}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
            submitLabel="Save Changes"
            isLoading={isUpdating}
          />
        </div>
      </div>
    );
  }

  // Detail view
  return (
    <div className="card">
      <div className="card-header border-b border-soft-neutral">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="btn-ghost btn-sm flex items-center gap-1">
            <ArrowLeft size={16} />
            Back to list
          </button>
          {isActionable && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-ghost btn-sm flex items-center gap-1"
            >
              <Edit2 size={14} />
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="card-body space-y-4">
        {/* Header with status */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                {deadline.status.replace('_', ' ')}
              </span>
              {deadline.isRecurring && (
                <span className="flex items-center gap-1 text-xs text-muted">
                  <RotateCcw size={12} />
                  Recurring
                </span>
              )}
              {deadline.creationSource === 'ai_extracted' && (
                <span className="flex items-center gap-1 text-xs text-purple-600">
                  <Sparkles size={12} />
                  AI-extracted
                </span>
              )}
            </div>
            <h2 className="text-xl font-semibold text-ink">{deadline.title}</h2>
          </div>
        </div>

        {/* Due date and priority */}
        <div className={`p-4 rounded-lg border-l-4 ${priorityStyle.border} ${priorityStyle.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <Calendar size={18} className={priorityStyle.text} />
                <span className={`font-medium ${priorityStyle.text}`}>{formatDate(deadline.dueDate)}</span>
              </span>
              <span className="flex items-center gap-2">
                <Clock size={18} className={priorityStyle.text} />
                <span className={`font-medium ${priorityStyle.text}`}>{formatDaysRemaining(deadline.dueDate)}</span>
              </span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
              {priorityLabel} Priority
            </span>
          </div>
        </div>

        {/* Type */}
        <div>
          <p className="text-sm text-muted mb-1">Type</p>
          <p className="text-ink font-medium">{typeLabel}</p>
        </div>

        {/* Description */}
        {deadline.description && (
          <div>
            <p className="text-sm text-muted mb-1">Description</p>
            <p className="text-ink whitespace-pre-wrap">{deadline.description}</p>
          </div>
        )}

        {/* Related Document */}
        {relatedDocument && (
          <div>
            <p className="text-sm text-muted mb-1">Related Document</p>
            <div className="flex items-center gap-2 text-ink">
              <FileText size={16} className="text-muted" />
              <span>{relatedDocument.fileName}</span>
              <span className="badge badge-secondary text-xs">{relatedDocument.documentType}</span>
            </div>
          </div>
        )}

        {/* Recurrence */}
        {deadline.isRecurring && deadline.recurrencePattern && (
          <div>
            <p className="text-sm text-muted mb-1">Recurrence</p>
            <p className="text-ink">
              Repeats {RECURRENCE_PATTERNS.find((p) => p.value === deadline.recurrencePattern)?.label.toLowerCase()}
            </p>
          </div>
        )}

        {/* Alerts */}
        {deadline.alerts.length > 0 && (
          <div>
            <p className="text-sm text-muted mb-2 flex items-center gap-1">
              <Bell size={14} />
              Reminders
            </p>
            <div className="space-y-1">
              {deadline.alerts.map((alert) => (
                <div key={alert.alertId} className="text-sm text-ink">
                  {alert.daysBeforeDeadline} day{alert.daysBeforeDeadline !== 1 ? 's' : ''} before via{' '}
                  {alert.channels.join(', ')}
                  {!alert.enabled && <span className="text-muted ml-1">(disabled)</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed info */}
        {deadline.status === 'completed' && deadline.completedAt && (
          <div>
            <p className="text-sm text-muted mb-1">Completed</p>
            <p className="text-ink">
              {new Date(deadline.completedAt._seconds * 1000).toLocaleString()}
            </p>
          </div>
        )}

        {/* Actions */}
        {isActionable && (
          <div className="flex flex-wrap gap-3 pt-4 border-t border-soft-neutral">
            <button
              onClick={handleComplete}
              disabled={actionLoading !== null}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {actionLoading === 'complete' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
              Mark Complete
            </button>
            <button
              onClick={handleDismiss}
              disabled={actionLoading !== null}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              {actionLoading === 'dismiss' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <X size={16} />
              )}
              Dismiss
            </button>
            {onExport && (
              <button
                onClick={() => onExport(deadline)}
                disabled={actionLoading !== null}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="detail-export-button"
              >
                <CalendarPlus size={16} />
                Export to Calendar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeadlineDetail;
